import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import { authFetch, changePassword } from "../../services/authService";
import { validatePassword } from "../../utils/authUtils";
import LoadingButton from "../../components/LoadingButton/LoadingButton";
import "./EmployeeSubPage.css";

const PersonalInfo = () => {
  const [personalInfo, setPersonalInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [apiCalling, setApiCalling] = useState(false);
  const navigate = useNavigate();
  const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
  const userId =
    userInfo.user_id || parseInt(localStorage.getItem("userId") || "0");

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("tr-TR");
    } catch (e) {
      return dateString;
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError("");
    setPasswordSuccess("");
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Tüm alanları doldurunuz.");
      setPasswordLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Yeni şifreler eşleşmiyor.");
      setPasswordLoading(false);
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError("Yeni şifre mevcut şifreden farklı olmalıdır.");
      setPasswordLoading(false);
      return;
    }
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      setPasswordLoading(false);
      return;
    }
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result.success) {
        setPasswordSuccess(result.message);
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess("");
        }, 2000);
      } else {
        setPasswordError(result.error);
      }
    } catch (error) {
      setPasswordError(
        error.message || "Bir hata oluştu. Lütfen tekrar deneyin."
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  useEffect(() => {
    const fetchPersonalInfo = async () => {
      if (apiCalling) return;
      if (userInfo && userInfo.name) {
        setPersonalInfo(userInfo);
        setLoading(false);
        return;
      }
      if (!userId || userId <= 0) {
        setError("Geçerli bir kullanıcı ID bulunamadı");
        setLoading(false);
        return;
      }
      try {
        setApiCalling(true);
        const response = await authFetch(
          `https://bilimkonya-rfid.onrender.com/api/employees/${userId}/personal-info`
        );
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Kullanıcı bilgileri bulunamadı.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPersonalInfo(data);
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      } finally {
        setApiCalling(false);
      }
    };
    let mounted = true;
    if (mounted) {
      fetchPersonalInfo();
    }
    return () => {
      mounted = false;
    };
  }, [userId]);

  const goBack = () => {
    navigate("/employee");
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (passwordError) {
      setPasswordError("");
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowPasswords({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setPasswordError("");
    setPasswordSuccess("");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <i className="fas fa-exclamation-triangle"></i>
        <p>Hata: {error}</p>
        <button
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Yeniden Dene
        </button>
        <button className="back-button" onClick={goBack}>
          Geri Dön
        </button>
      </div>
    );
  }

  return (
    <div className="employee-subpage">
      <div className="employee-container">
        <div className="subpage-header">
          <h2>Bilgilerimi Görüntüle</h2>
        </div>
        {Object.keys(personalInfo).length > 0 ? (
          <div className="personal-info-container">
            <div className="profile-card">
              <div className="profile-avatar">
                <i className="fas fa-user"></i>
              </div>
              <div className="profile-name">
                {personalInfo.name} {personalInfo.surname}
              </div>
              <div className="profile-position">
                {personalInfo.role_name || "Personel"}
              </div>
              <button
                className="change-password-btn"
                onClick={() => setShowPasswordModal(true)}
              >
                <i className="fas fa-lock"></i>
                Şifremi Değiştir
              </button>
            </div>
            <div className="info-sections">
              <div className="info-section">
                <h3>Kişisel Bilgiler</h3>
                <div className="info-item">
                  <span className="info-label">Adı:</span>
                  <span className="info-value">{personalInfo.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Soyadı:</span>
                  <span className="info-value">{personalInfo.surname}</span>
                </div>
                {personalInfo.tc_no && (
                  <div className="info-item">
                    <span className="info-label">T.C. Kimlik No:</span>
                    <span className="info-value">{personalInfo.tc_no}</span>
                  </div>
                )}
                {personalInfo.birth_date && (
                  <div className="info-item">
                    <span className="info-label">Doğum Tarihi:</span>
                    <span className="info-value">
                      {formatDate(personalInfo.birth_date)}
                    </span>
                  </div>
                )}
              </div>
              <div className="info-section">
                <h3>İletişim Bilgileri</h3>
                <div className="info-item">
                  <span className="info-label">E-posta:</span>
                  <span className="info-value">{personalInfo.email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Telefon:</span>
                  <span className="info-value">{personalInfo.phone}</span>
                </div>
                {personalInfo.address && (
                  <div className="info-item">
                    <span className="info-label">Adres:</span>
                    <span className="info-value">{personalInfo.address}</span>
                  </div>
                )}
              </div>
              <div className="info-section">
                <h3>İş Bilgileri</h3>
                {personalInfo.employee_no && (
                  <div className="info-item">
                    <span className="info-label">Personel No:</span>
                    <span className="info-value">
                      {personalInfo.employee_no}
                    </span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Pozisyon:</span>
                  <span className="info-value">
                    {personalInfo.role_name || "Personel"}
                  </span>
                </div>
                {personalInfo.department && (
                  <div className="info-item">
                    <span className="info-label">Departman:</span>
                    <span className="info-value">
                      {personalInfo.department}
                    </span>
                  </div>
                )}
                {personalInfo.hire_date && (
                  <div className="info-item">
                    <span className="info-label">İşe Başlama Tarihi:</span>
                    <span className="info-value">
                      {formatDate(personalInfo.hire_date)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="no-data">
            <i className="fas fa-user-slash"></i>
            <p>Kişisel bilgileriniz bulunamadı.</p>
          </div>
        )}
      </div>
      {showPasswordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <div className="password-modal-header">
              <h3>Şifre Değiştir</h3>
              <button className="close-modal-btn" onClick={closePasswordModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="password-field">
                <label htmlFor="currentPassword">Mevcut Şifre *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.currentPassword ? "text" : "password"}
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Mevcut şifrenizi girin"
                    required
                  />
                  <i className="fas fa-lock password-icon"></i>
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => togglePasswordVisibility("currentPassword")}
                    aria-label={
                      showPasswords.currentPassword
                        ? "Şifreyi gizle"
                        : "Şifreyi göster"
                    }
                  >
                    <i
                      className={
                        showPasswords.currentPassword
                          ? "fas fa-eye-slash"
                          : "fas fa-eye"
                      }
                    ></i>
                  </button>
                </div>
              </div>
              <div className="password-field">
                <label htmlFor="newPassword">Yeni Şifre *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.newPassword ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Yeni şifrenizi girin"
                    required
                  />
                  <i className="fas fa-key password-icon"></i>
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => togglePasswordVisibility("newPassword")}
                    aria-label={
                      showPasswords.newPassword
                        ? "Şifreyi gizle"
                        : "Şifreyi göster"
                    }
                  >
                    <i
                      className={
                        showPasswords.newPassword
                          ? "fas fa-eye-slash"
                          : "fas fa-eye"
                      }
                    ></i>
                  </button>
                </div>
                <div className="password-requirements">
                  <p>Şifre gereksinimleri:</p>
                  <ul>
                    <li
                      className={
                        passwordForm.newPassword.length >= 8 ? "valid" : ""
                      }
                    >
                      En az 8 karakter
                    </li>
                    <li
                      className={
                        /[A-Z]/.test(passwordForm.newPassword) ? "valid" : ""
                      }
                    >
                      En az 1 büyük harf
                    </li>
                    <li
                      className={
                        /[a-z]/.test(passwordForm.newPassword) ? "valid" : ""
                      }
                    >
                      En az 1 küçük harf
                    </li>
                    <li
                      className={
                        /\d/.test(passwordForm.newPassword) ? "valid" : ""
                      }
                    >
                      En az 1 rakam
                    </li>
                    <li
                      className={
                        /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword)
                          ? "valid"
                          : ""
                      }
                    >
                      En az 1 özel karakter
                    </li>
                  </ul>
                </div>
              </div>
              <div className="password-field">
                <label htmlFor="confirmPassword">Yeni Şifre Tekrar *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.confirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Yeni şifrenizi tekrar girin"
                    required
                  />
                  <i className="fas fa-check-double password-icon"></i>
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => togglePasswordVisibility("confirmPassword")}
                    aria-label={
                      showPasswords.confirmPassword
                        ? "Şifreyi gizle"
                        : "Şifreyi göster"
                    }
                  >
                    <i
                      className={
                        showPasswords.confirmPassword
                          ? "fas fa-eye-slash"
                          : "fas fa-eye"
                      }
                    ></i>
                  </button>
                </div>
                {passwordForm.confirmPassword &&
                  passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <div className="password-match-error">
                      Şifreler eşleşmiyor
                    </div>
                  )}
                {passwordForm.confirmPassword &&
                  passwordForm.newPassword === passwordForm.confirmPassword && (
                    <div className="password-match-success">
                      Şifreler eşleşiyor ✓
                    </div>
                  )}
              </div>
              {passwordError && (
                <div className="password-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="password-success">
                  <i className="fas fa-check-circle"></i>
                  {passwordSuccess}
                </div>
              )}
              <div className="password-modal-buttons">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closePasswordModal}
                  disabled={passwordLoading}
                >
                  İptal
                </button>
                <LoadingButton
                  type="submit"
                  className="save-btn"
                  isLoading={passwordLoading}
                  disabled={
                    passwordLoading ||
                    !passwordForm.currentPassword ||
                    !passwordForm.newPassword ||
                    !passwordForm.confirmPassword
                  }
                >
                  {passwordLoading ? "Kaydediliyor..." : "Şifreyi Kaydet"}
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfo;
